import { EXPERIENCE_ID } from '@atlaskit/editor-common/experiences/consts';
import { Experience } from '@atlaskit/editor-common/experiences/Experience';
import { ExperienceCheckTimeout } from '@atlaskit/editor-common/experiences/ExperienceCheckTimeout';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AiSuggestionsPlugin } from '../aiSuggestionsPluginType';
import { aiSuggestionsPluginKey } from '../pm-plugins/main';

export const REJECT_SUGGESTION_TIMEOUT_MS = 10_000;

export type RejectSuggestionExperience = {
	isRejected: (id: string) => boolean;
	plugin: () => SafePlugin<void>;
	start: (method: string) => Experience;
};

/** Tracks each reject attempt through local removal and the status API response. */
export function createRejectSuggestionExperience(
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined,
): RejectSuggestionExperience {
	const pending = new Set<Experience>();
	let editorView: EditorView | undefined;
	return {
		isRejected: (id) => {
			const state = editorView && aiSuggestionsPluginKey.getState(editorView.state);
			// execute can return true even when the transaction was filtered.
			return !!state && !state.suggestionById.has(id);
		},
		start: (method) => {
			const experience = new Experience(EXPERIENCE_ID.MENU_ACTION, {
				action: 'rejectSuggestion',
				actionSubjectId: 'aiSuggestions',
				sampleRate: 1,
				checks: [new ExperienceCheckTimeout({ durationMs: REJECT_SUGGESTION_TIMEOUT_MS })],
				dispatchAnalyticsEvent: (event) => {
					if (event.attributes?.experienceStatus !== 'started') {
						pending.delete(experience);
					}
					api?.analytics?.actions.fireAnalyticsEvent(event);
				},
			});
			pending.add(experience);
			experience.start({ method });
			return experience;
		},
		plugin: () =>
			new SafePlugin<void>({
				view: (view) => {
					editorView = view;
					return {
						destroy: () => {
							for (const experience of pending) {
								experience.abort({ reason: 'editorDestroyed' });
							}
							editorView = undefined;
						},
					};
				},
			}),
	};
}
